import { useState, useEffect } from "react"
import { Pencil, Bold, Italic, List, ListOrdered, Heading1, Heading2, LinkIcon, ArrowRight } from "lucide-react"
import { toast } from "react-toastify"
import { getPrivacyPolicy, updatePrivacyPolicy } from "../../services/api"
import { Button } from "../../components/ui/button"
import { Card, CardContent } from "../../components/ui/card"
import { useEditor, EditorContent, type Editor } from "@tiptap/react"
import StarterKit from "@tiptap/starter-kit"
import Link from "@tiptap/extension-link"
import Placeholder from "@tiptap/extension-placeholder"
import TextStyle from "@tiptap/extension-text-style"
import Color from "@tiptap/extension-color"

// Helper function to unescape HTML from API
const unescapeHtml = (html: string) => {
  if (!html) return ""

  // Unescape the JSON string if needed
  return html.replace(/\\([\s\S])|(")/g, "$1$2")
}

// Helper function to add CSS classes to HTML elements
const addClassesToHtml = (html: string) => {
  if (!html) return ""

  // Add classes to elements that need styling
  return (
    html
      // Style h1 elements
      .replace(/<h1>/g, '<h1 class="text-2xl font-serif text-[#b05d5d] mb-4">')

      // Style h2 elements with arrow
      .replace(
        /<h2>→/g,
        '<h2 class="text-xl font-serif text-[#b05d5d] flex items-center mt-8 mb-4"><span class="heading-arrow mr-2">→</span>',
      )

      // Style paragraphs
      .replace(/<p>/g, '<p class="mb-4">')

      // Style lists
      .replace(/<ul>/g, '<ul class="list-disc pl-8 mb-4 space-y-2">')

      // Style list items
      .replace(/<li><p>/g, '<li class="mb-2"><p>')

      // Make sure links have the right color
      .replace(/<a /g, '<a class="text-[#b05d5d]" ')
  )
}

// Types for our privacy policy data
export interface PrivacyPolicyData {
  id: string
  title: string
  content: string
  created_at: string
  updated_at: string
}

const MenuBar = ({ editor }: { editor: Editor | null }) => {
  if (!editor) {
    return null
  }

  const addLink = () => {
    const previousUrl = editor.getAttributes("link").href
    const url = window.prompt("URL", previousUrl)

    // cancelled
    if (url === null) {
      return
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run()
      return
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run()
  }

  const addArrowHeading = () => {
    // First toggle the heading
    editor.chain().focus().toggleHeading({ level: 2 }).run()

    // If heading was just activated, add the arrow
    if (editor.isActive("heading", { level: 2 })) {
      // Get current cursor position
      const { from } = editor.state.selection

      // Insert arrow at current position
      editor.chain().insertContentAt(from, "→ ").run()
    }
  }

  // Format the selected text as a styled list item with emphasis
  const formatListItem = () => {
    const selectedText = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to)

    if (selectedText) {
      // First check if we're already in a list
      const isInList = editor.isActive("bulletList") || editor.isActive("orderedList")

      if (!isInList) {
        // Create a bullet list first
        editor.chain().focus().toggleBulletList().run()
      }

      // Now format the text with emphasis
      editor.chain().focus().setColor("#b05d5d").toggleBold().run()
    } else {
      // If no text is selected, just create a normal list
      editor.chain().focus().toggleBulletList().run()
    }
  }

  return (
    <div className="border border-input rounded-md p-1 mb-2 flex flex-wrap gap-1">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={editor.isActive("bold") ? "bg-muted" : ""}
        type="button"
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={editor.isActive("italic") ? "bg-muted" : ""}
        type="button"
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={editor.isActive("heading", { level: 1 }) ? "bg-muted" : ""}
        type="button"
        title="Heading 1"
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={addArrowHeading}
        className={editor.isActive("heading", { level: 2 }) ? "bg-muted" : ""}
        type="button"
        title="Heading 2 with Arrow"
      >
        <div className="flex items-center">
          <Heading2 className="h-4 w-4" />
          <ArrowRight className="h-3 w-3 ml-0.5" />
        </div>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={formatListItem}
        className={editor.isActive("bulletList") ? "bg-muted" : ""}
        type="button"
        title="Bullet List"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive("orderedList") ? "bg-muted" : ""}
        type="button"
        title="Numbered List"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={addLink}
        className={editor.isActive("link") ? "bg-muted" : ""}
        type="button"
        title="Add Link"
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().setColor("#b05d5d").run()}
        className="text-[#b05d5d]"
        type="button"
        title="Brand Color"
      >
        <span className="w-4 h-4 bg-[#b05d5d] rounded-full"></span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().unsetColor().run()}
        type="button"
        title="Default Color"
      >
        <span className="w-4 h-4 bg-black rounded-full"></span>
      </Button>
    </div>
  )
}

export default function PrivacyPolicyEditor() {
  const initialPrivacyPolicyData: PrivacyPolicyData = {
    id: "",
    title: "",
    content: "",
    created_at: "",
    updated_at: "",
  }

  const [policyData, setPolicyData] = useState<PrivacyPolicyData>(initialPrivacyPolicyData)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [rawHtml, setRawHtml] = useState<string>("")

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-[#b05d5d] underline",
        },
      }),
      Placeholder.configure({
        placeholder: "Enter your privacy policy content here...",
      }),
      TextStyle,
      Color,
    ],
    content: policyData.content || "",
    onUpdate: ({ editor }) => {
      // Store the raw HTML directly
      const html = editor.getHTML()
      setRawHtml(html)

      // Only update the content, don't reset the entire policyData object
      // This helps preserve cursor position
      setPolicyData((prev) => ({
        ...prev,
        content: html,
      }))
    },
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[400px] p-4",
      },
    },
    // This ensures HTML content is properly parsed
    enableInputRules: true,
    enablePasteRules: true,
  })

  // Load data from API on component mount
  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        setIsLoading(true)
        const response = await getPrivacyPolicy()

        if (response.data && response.data.data) {
          // Use the first policy in the array
          const policy = response.data.data[0]

          // Unescape the HTML content
          const unescapedContent = unescapeHtml(policy.content)

          // Store the raw HTML
          setRawHtml(unescapedContent)

          setPolicyData({
            ...policy,
            content: unescapedContent,
          })
        } else {
          // Initialize with empty data if no policy exists
          setPolicyData(initialPrivacyPolicyData)
        }
        setError(null)
      } catch (err) {
        setError("Failed to load privacy policy data")
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchPrivacyPolicy()
  }, [])

  // Update editor content when editing state changes
  useEffect(() => {
    if (editor && policyData.content && !editor.isDestroyed && isEditing) {
      // Only update content if it's different from current editor content
      // This prevents cursor jumping
      const currentContent = editor.getHTML()
      if (currentContent !== policyData.content) {
        // Use setContent with specific options
        editor.commands.setContent(policyData.content)
      }
    }
  }, [isEditing, editor, policyData.id]) // Note: removed policyData.content from dependencies

  // Save changes to the API
  const saveChanges = async () => {
    try {
      setIsSaving(true)

      // Use the raw HTML directly to preserve all formatting
      const dataToSave = {
        content: rawHtml,
      }

      console.log("Saving content:", rawHtml)

      // Update existing policy
      if (policyData.id) {
        await updatePrivacyPolicy(policyData.id, dataToSave)
        toast.success("Privacy policy updated successfully!")
      }

      setIsEditing(false)
      setError(null)
    } catch (err) {
      setError("Failed to save privacy policy data")
      console.error(err)
      toast.error("Failed to save privacy policy")
    } finally {
      setIsSaving(false)
    }
  }

  const loadSampleContent = () => {
    if (editor) {
      // Set content
      editor.commands.setContent(samplePrivacyPolicy)

      // Store the raw HTML
      setRawHtml(samplePrivacyPolicy)

      // Update state after editor content is set
      setPolicyData((prev) => ({
        ...prev,
        content: samplePrivacyPolicy,
      }))
    }
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="text-red-500 mb-4">{error}</div>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    )
  }

  // Add CSS classes to the content for display
  const displayContent = addClassesToHtml(policyData.content)

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-serif font-medium">Privacy Policy</h1>
          <p className="text-sm text-muted-foreground">Edit and update your website's privacy policy.</p>
        </div>
        {!isEditing ? (
          <Button
            variant="outline"
            className="border-[#B87775] text-[#B87775] hover:bg-rose-50"
            onClick={() => setIsEditing(true)}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        ) : (
          <Button
            variant="outline"
            className="border-[#B87775] text-[#B87775] hover:bg-rose-50"
            onClick={loadSampleContent}
          >
            Load Sample Content
          </Button>
        )}
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <Card className="border-muted">
          <CardContent className="p-6 space-y-6">
            {/* Privacy Policy Content */}
            <div>
              <h2 className="text-lg font-medium mb-4">Privacy Policy content</h2>
              <div className="space-y-4">
                <div>
                  {isEditing ? (
                    <div className="border rounded-md">
                      <MenuBar editor={editor} />
                      <EditorContent editor={editor} />
                    </div>
                  ) : (
                    <div
                      className="p-4 border rounded-md bg-muted/20 min-h-[400px] privacy-policy-content"
                      dangerouslySetInnerHTML={{ __html: displayContent || "No privacy policy content provided" }}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Save Button */}
            {isEditing && (
              <div className="flex justify-center">
                <Button
                  onClick={saveChanges}
                  disabled={isSaving}
                  className="bg-[#B87775] hover:bg-[#a56967] text-white px-8 py-2 uppercase tracking-wider"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// Sample privacy policy content
const samplePrivacyPolicy = `
<h1 class="text-2xl font-serif text-[#b05d5d] mb-4">Privacy Policy for Charmé</h1>
<p class="mb-4">Welcome to Charmé ("we," "our," or "us"). We value your privacy and are committed to protecting your personal information. This Privacy Policy outlines how we collect, use, and protect your data when you use our makeup booking and product delivery services.</p>

<h2 class="text-xl font-serif text-[#b05d5d] flex items-center mt-8 mb-4"><span class="heading-arrow mr-2">→</span> Information we collect</h2>
<p class="mb-4">We collect the following types of personal information when you use our website:</p>

<ul class="list-disc pl-8 mb-4 space-y-2">
  <li><span class="text-[#b05d5d] font-medium">Booking Details:</span> When you book a makeup session, we collect your full name, email address, phone number, and any additional details provided.</li>
  <li><span class="text-[#b05d5d] font-medium">Images:</span> We may request and store images uploaded by you during the booking process to assist in customizing your makeup session.</li>
  <li><span class="text-[#b05d5d] font-medium">Delivery Details:</span> If you purchase products from our online shop, we collect your shipping address, phone number, and any relevant delivery instructions.</li>
  <li><span class="text-[#b05d5d] font-medium">Payment Information:</span> We do not store your payment details directly. All transactions are securely processed through third-party payment providers.</li>
</ul>

<h2 class="text-xl font-serif text-[#b05d5d] flex items-center mt-8 mb-4"><span class="heading-arrow mr-2">→</span> How We Use Your Information</h2>
<p class="mb-4">We use the collected information for the following purposes:</p>

<ul class="list-disc pl-8 mb-4 space-y-2">
  <li>To confirm and manage your makeup session bookings.</li>
  <li>To personalize and improve our services.</li>
  <li>To process and deliver product purchases.</li>
  <li>To communicate with you regarding your appointments, purchases, or promotional offers (if opted in).</li>
  <li>To comply with legal obligations and prevent fraudulent activities.</li>
</ul>

<h2 class="text-xl font-serif text-[#b05d5d] flex items-center mt-8 mb-4"><span class="heading-arrow mr-2">→</span> Contact Us</h2>
<p class="mb-4">For any questions or concerns regarding this Privacy Policy, please contact us at: <a href="mailto:charmext@gmail.com" class="text-[#b05d5d]">charmext@gmail.com</a></p>

<p class="">By using our website, you consent to the terms outlined in this <span class="text-[#b05d5d]">Privacy Policy</span>.</p>
`

